import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Instagram, Facebook, Linkedin, ArrowUpRight } from 'lucide-react'

const About = () => {
  const { t } = useTranslation()
  const owner = {
    name: 'Mostafa',
    role: t('about.role', 'CEO & Automation Expert'),
    bio: t('about.bio', 'Expert in designing complex workflows using n8n and building AI solutions for business growth.'),
    image: '/mostafa.png',
    instagram: 'https://www.instagram.com/m5hot',
    facebook: 'https://www.facebook.com/m5hot',
    linkedin: 'https://www.linkedin.com/in/mostafa-elsheikh-44854b322/'
  }

  return (
    <section id="about" className="py-24 bg-[#0a0a0f] relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/5 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/5 blur-[120px] rounded-full translate-y-1/2 -translate-x-1/2"></div>

      <div className="container-custom relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-purple-400 font-semibold tracking-wider uppercase mb-4 block"
          >
            {t('about.badge', 'Our Story')}
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6"
            >
              {t('about.heading1', 'We Build the')} <span className="text-purple-400">{t('about.heading2', 'Future of Automation')}</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-gray-400 text-lg leading-relaxed"
            >
              {t('about.mission', "At Nodatx, we believe time is the most valuable resource for businesses. Our mission is to empower companies to grow by automating repetitive tasks and building intelligent systems that work for you, so you can focus on what truly matters.")}
            </motion.p>
        </div>

        {/* Single Centered Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="max-w-md mx-auto"
        >
          <div className="relative bg-gradient-to-br from-[#1a1a2e] to-[#12121f] border border-white/10 rounded-3xl p-8 hover:border-purple-500/40 transition-all duration-500 group">
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-purple-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Content */}
            <div className="relative z-10">
              {/* Image Container */}
              <div className="relative w-40 h-40 mx-auto mb-6">
                {/* Animated Border */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500 via-purple-500 to-purple-500 animate-spin-slow opacity-50 group-hover:opacity-100 transition-opacity duration-500" style={{ animationDuration: '3s' }}></div>
                <div className="absolute inset-1 rounded-full bg-[#0a0a0f]"></div>
                <div className="relative w-full h-full rounded-full overflow-hidden border-2 border-purple-500/30">
                  <img 
                    src={owner.image} 
                    alt={owner.name} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(owner.name)}&background=11131a&color=8B5CF6&size=200`
                    }}
                  />
                </div>
              </div>
              
              {/* Info */}
              <div className="text-center">
                <h3 className="text-2xl font-bold text-white mb-2">{owner.name}</h3>
                <p className="text-purple-400 font-semibold mb-4 text-sm uppercase tracking-wider">{owner.role}</p>
                <p className="text-gray-400 mb-6 text-sm leading-relaxed">
                  {owner.bio}
                </p>
                
                {/* Social Links */}
                <div className="flex justify-center gap-3">
                  {owner.linkedin && (
                    <a 
                      href={owner.linkedin} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="flex items-center gap-2 px-4 py-2 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/20 rounded-full text-purple-400 hover:text-purple-300 transition-all text-sm"
                    >
                      <Linkedin className="w-4 h-4" />
                      <span>LinkedIn</span>
                      <ArrowUpRight className="w-3 h-3" />
                    </a>
                  )}
                  {owner.instagram && (
                    <a 
                      href={owner.instagram} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-all"
                    >
                      <Instagram className="w-4 h-4" />
                    </a>
                  )}
                  {owner.facebook && (
                    <a 
                      href={owner.facebook} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-all"
                    >
                      <Facebook className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default About